import { getSessionSeed } from "./marketplace-session";
import type { MarketplaceSort } from "@/components/Marketplace/types";

export type SortApiParams = {
  sortBy?: "random" | "releaseDate" | "popular" | "price";
  sortDirection?: "asc" | "desc";
  seed?: string;
};

export const SORT_OPTIONS: { value: MarketplaceSort; label: string }[] = [
  { value: "discover", label: "Discover" },
  { value: "new-releases", label: "New Releases" },
  { value: "most-popular", label: "Most Popular" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

export function mapSortToApiParams(
  sort: MarketplaceSort | undefined,
): SortApiParams {
  switch (sort) {
    case "new-releases":
      return { sortBy: "releaseDate", sortDirection: "desc" };
    case "most-popular":
      return { sortBy: "popular", sortDirection: "desc" };
    case "price-asc":
      return { sortBy: "price", sortDirection: "asc" };
    case "price-desc":
      return { sortBy: "price", sortDirection: "desc" };
    case "discover":
    default:
      return { sortBy: "random", seed: getSessionSeed() };
  }
}

export function deClusterArtists<T extends { creator: { creatorId: string } }>(
  items: T[],
  maxConsecutive = 2,
): T[] {
  if (items.length === 0) return items;

  const result: T[] = [];
  const pending: T[] = [...items];

  while (pending.length > 0) {
    const lastCreatorId =
      result.length > 0 ? result[result.length - 1].creator.creatorId : null;
    const consecutiveCount = getTrailingConsecutiveCount(result, lastCreatorId);

    if (consecutiveCount < maxConsecutive) {
      result.push(pending.shift()!);
    } else {
      // Current item would exceed the limit — find the next item from a different artist
      const altIndex = pending.findIndex(
        (item) => item.creator.creatorId !== lastCreatorId,
      );
      if (altIndex === -1) {
        // All remaining items are same artist — just append them
        result.push(...pending.splice(0));
      } else {
        result.push(...pending.splice(altIndex, 1));
      }
    }
  }

  return result;
}

function getTrailingConsecutiveCount<
  T extends { creator: { creatorId: string } },
>(items: T[], creatorId: string | null): number {
  if (!creatorId) return 0;
  let count = 0;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].creator.creatorId === creatorId) count++;
    else break;
  }
  return count;
}
